import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ArrowRight, Calendar, ChevronDown, Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  ISSUE_SEVERITY_FILTER_OPTIONS,
  ISSUE_STATUS_FILTER_OPTIONS,
  issueSeverityLabel,
  issueStatusLabel,
  type IssueDerivedStatus,
  type IssueSeverity,
  type IssueStatus,
} from '@/shared/constants/issue-status'

import { getSiteIssues } from '../../api/siteApi'

// ─── Severity & status palette (redesign tokens) ──────────────────────────

interface SeverityTone {
  accent: string
  tileBg: string
  tileFg: string
  pill: string
}

const SEVERITY_TONE: Record<IssueSeverity, SeverityTone> = {
  critical: {
    accent: 'bg-danger',
    tileBg: 'from-[#fdf3f1] to-danger-soft',
    tileFg: 'text-danger',
    pill: 'bg-danger-soft text-danger',
  },
  non_critical: {
    accent: 'bg-warning',
    tileBg: 'from-[#fef9ec] to-warning-soft',
    tileFg: 'text-warning',
    pill: 'bg-warning-soft text-warning',
  },
}

const STATUS_PILL: Record<IssueStatus, string> = {
  open: 'bg-warning-soft text-warning',
  pending: 'bg-brand-soft text-brand',
  completed: 'bg-success-soft text-success',
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

// ─── Toolbar select ───────────────────────────────────────────────────────

function ToolbarSelect<T extends string>({
  value,
  options,
  onChange,
  width = 'w-[150px]',
}: {
  value: T | ''
  options: ReadonlyArray<{ value: T | ''; label: string }>
  onChange: (v: T | '') => void
  width?: string
}) {
  return (
    <div className={cn('relative shrink-0', width)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className="border-line text-ink-2 hover:border-[#cdd6e8] focus:border-brand-3 font-display h-8 w-full cursor-pointer appearance-none rounded-[7px] border bg-white py-0 pr-7 pl-2.5 text-[13px] font-medium outline-none focus:shadow-[0_0_0_3px_rgba(22,93,255,0.10)]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={11}
        className="text-muted-text pointer-events-none absolute top-1/2 right-2 -translate-y-1/2"
      />
    </div>
  )
}

// ─── Issue row ────────────────────────────────────────────────────────────

interface IssueLike {
  id: string
  title: string
  severity: IssueSeverity
  status: IssueStatus
  created_at: string
  survey_ref?: string
  section_title?: string
}

function IssueRow({ issue, onView }: { issue: IssueLike; onView: () => void }) {
  const sev = SEVERITY_TONE[issue.severity] ?? SEVERITY_TONE.non_critical

  return (
    <article className="border-line relative isolate flex items-center gap-3 overflow-hidden rounded-[11px] border bg-white px-3.5 py-[11px] transition-all duration-150 hover:-translate-y-px hover:border-[#dde3f0] hover:shadow-[0_10px_24px_-14px_rgba(11,21,48,0.14)]">
      <span
        aria-hidden="true"
        className={cn('absolute top-0 bottom-0 left-0 w-[3px] opacity-85', sev.accent)}
      />

      {/* Severity tile */}
      <div
        className={cn(
          'grid size-[38px] shrink-0 place-items-center rounded-[9px] bg-gradient-to-br',
          sev.tileBg,
          sev.tileFg,
        )}
      >
        <AlertTriangle size={16} strokeWidth={2} />
      </div>

      {/* Body */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h3 className="text-ink text-[15px] leading-[1.25] font-bold tracking-[-0.005em]">
          {issue.title}
        </h3>
        <div className="text-ink-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
          <span className="inline-flex items-center gap-1">
            <Calendar size={11} strokeWidth={2} className="text-muted-text shrink-0" />
            {formatDate(issue.created_at)}
          </span>
          {issue.survey_ref && (
            <span className="inline-flex items-center gap-1.5">
              From
              <span className="bg-brand-tint text-brand inline-flex items-center rounded-[4px] px-1.5 py-px text-[10.5px] font-bold tabular-nums">
                {issue.survey_ref}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Badges + View */}
      <div className="flex shrink-0 items-center gap-1.5">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10.5px] font-bold tracking-[0.04em] uppercase',
            sev.pill,
          )}
        >
          <span className="size-1 rounded-full bg-current" />
          {issueSeverityLabel(issue.severity)}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10.5px] font-bold tracking-[0.04em] uppercase',
            STATUS_PILL[issue.status] ?? 'bg-line-2 text-muted-text',
          )}
        >
          <span className="size-1 rounded-full bg-current" />
          {issueStatusLabel(issue.status)}
        </span>
        <button
          type="button"
          onClick={onView}
          className="bg-brand-gradient inline-flex h-[30px] items-center gap-1.5 rounded-full px-3.5 text-[12px] font-semibold text-white shadow-[0_4px_10px_-4px_rgba(11,59,145,0.55)] transition hover:-translate-y-px hover:shadow-[0_6px_14px_-4px_rgba(11,59,145,0.65)]"
        >
          View
          <ArrowRight size={11} strokeWidth={2.4} />
        </button>
      </div>
    </article>
  )
}

// ─── Tab ──────────────────────────────────────────────────────────────────

export function SiteIssuesTab({ siteId }: { siteId: string }) {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<IssueDerivedStatus | ''>('')
  const [severity, setSeverity] = useState<IssueSeverity | ''>('')
  const [category, setCategory] = useState('')

  const { data: issuesData, isLoading } = useQuery({
    queryKey: ['site-issues', siteId, status, severity],
    queryFn: () =>
      getSiteIssues(siteId, {
        ...(status ? { status } : {}),
        ...(severity ? { severity } : {}),
      }),
    enabled: !!siteId,
  })

  const issues: IssueLike[] = Array.isArray(issuesData)
    ? (issuesData as IssueLike[])
    : ((issuesData as { issues?: IssueLike[] } | undefined)?.issues ?? [])

  const sectionTitles = Array.from(
    new Set(issues.map((i) => i.section_title).filter(Boolean)),
  ) as string[]

  const categoryOptions: { value: string; label: string }[] = [
    { value: '', label: 'All categories' },
    ...sectionTitles.map((t) => ({ value: t, label: t })),
  ]

  const searchTerm = search.trim().toLowerCase()
  const filtered = issues.filter((i) => {
    if (searchTerm && !i.title.toLowerCase().includes(searchTerm)) {
      return false
    }
    if (category && i.section_title !== category) {
      return false
    }
    return true
  })

  // Group by section_title (preserve insertion order)
  const groups = filtered.reduce<Record<string, IssueLike[]>>((acc, issue) => {
    const key = issue.section_title ?? 'Other'
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key]!.push(issue)
    return acc
  }, {})
  const groupEntries = Object.entries(groups)

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="border-line-2 flex flex-wrap items-center gap-2.5 border-b px-3.5 py-3">
        <div className="focus-within:border-brand-3 focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(22,93,255,0.10)] inline-flex h-8 w-[240px] shrink-0 items-center gap-1.5 rounded-[8px] border border-transparent bg-line-2 px-3 transition-colors">
          <Search size={13} className="text-muted-text shrink-0" />
          <input
            type="text"
            placeholder="Search issues"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-ink placeholder:text-muted-text-2 h-full flex-1 border-0 bg-transparent text-[13px] outline-none"
          />
        </div>
        <ToolbarSelect
          value={status}
          options={ISSUE_STATUS_FILTER_OPTIONS}
          onChange={setStatus}
          width="w-[170px]"
        />
        <ToolbarSelect
          value={severity}
          options={ISSUE_SEVERITY_FILTER_OPTIONS}
          onChange={setSeverity}
          width="w-[150px]"
        />
        <ToolbarSelect value={category} options={categoryOptions} onChange={setCategory} />
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3.5 p-3.5">
        {isLoading && (
          <div className="text-muted-text py-10 text-center text-[13px] font-medium">
            Loading issues…
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="text-muted-text py-10 text-center text-[13px] font-medium">
            {searchTerm || status || severity || category
              ? 'No issues match your filters.'
              : 'No issues for this site yet.'}
          </div>
        )}
        {!isLoading &&
          groupEntries.map(([section, sectionIssues], i) => {
            const openCount = sectionIssues.filter(
              (it) => it.status !== 'completed',
            ).length
            return (
              <section key={section} className="flex flex-col gap-2">
                {/* Group head */}
                <div className="flex items-center gap-2.5 px-1 pb-0.5">
                  <span className="bg-brand-tint text-brand grid size-6 shrink-0 place-items-center rounded-[7px] text-[10px] font-extrabold tabular-nums">
                    {i + 1}
                  </span>
                  <span className="text-brand text-[11px] font-bold tracking-[0.1em] uppercase">
                    {section}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-bold',
                      openCount > 0
                        ? 'bg-danger-soft text-danger'
                        : 'bg-success-soft text-success',
                    )}
                  >
                    {openCount > 0 ? `${openCount} open` : 'all completed'}
                  </span>
                  <span
                    aria-hidden="true"
                    className="h-px flex-1"
                    style={{
                      background:
                        'linear-gradient(90deg, var(--color-line) 0%, transparent 100%)',
                    }}
                  />
                </div>

                {sectionIssues.map((issue) => (
                  <IssueRow
                    key={issue.id}
                    issue={issue}
                    onView={() =>
                      navigate({ to: '/issues/$issueId', params: { issueId: issue.id } })
                    }
                  />
                ))}
              </section>
            )
          })}
      </div>
    </div>
  )
}
